import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import Select from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import {
  addAdminService,
  updateAdminService,
  getByIdAdminService,
} from '@/services/AdminServices';
import { getRoleService } from '@/services/RoleServices';
import validationErrors from '@/lib/validationErrors';
import SidebarDrawer from '@/components/common/SidebarDrawer';
import { useDispatch } from 'react-redux';
import ToastMe from '@/components/ui/ToastMe';

// Toggle this if you want the role locked as a read-only badge instead of a dropdown.
const LOCK_ROLE_UI = false;

// Days
const dayOfWeekOptions = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const normalize = (s) =>
  String(s || '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

const containsChef = (r) => {
  const n1 = normalize(r?.name);
  const n2 = normalize(r?.value);
  const n3 = normalize(r?.key);
  return n1.includes('chef') || n2.includes('chef') || n3.includes('chef');
};

/* ─────────────────────────────────────────────────────────────
   Effective branch resolver (LocalStorage → Axios header → JWT)
   ───────────────────────────────────────────────────────────── */
function getEffectiveBranchId() {
  const isObjectId = (v) => typeof v === 'string' && /^[0-9a-fA-F]{24}$/.test(v);

  try {
    const ls = localStorage.getItem('branch_id');
    if (ls && ls !== 'all' && isObjectId(ls)) return ls;
  } catch {}

  try {
    const hdr = axios?.defaults?.headers?.common?.['X-Branch-Id'];
    if (hdr && hdr !== 'all' && isObjectId(hdr)) return hdr;
  } catch {}

  try {
    const tok =
      localStorage.getItem('customer_jwt') ||
      localStorage.getItem('customerToken') ||
      localStorage.getItem('jwt_token');
    if (tok) {
      const d = jwtDecode(tok) || {};
      const def = d?.branch_id;
      if (isObjectId(def)) return def;
    }
  } catch {}

  return null;
}

const AddEditDrawer = ({
  titleText,
  activeDrawer,
  setActiveDrawer,
  getTableData,
  editDataId = '',
}) => {
  const dispatch = useDispatch();

  const {
    setValue,
    getValues,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone_no: '',
      status: '1',
      password: '',
      confirm_password: '',
      role_id: '',     // forced to Chef
      salary: '',
      contractStartDate: '',
      contractEndDate: '',
      holidayDays: [],
      defaultInTime: '',
      defaultOutTime: '',
      penaltyRate: '',
      biometricId: '',
    },
  });

  const [itemStatus, setItemStatus]           = useState('1');
  const [isLoading, setIsLoading]             = useState(false);
  const [isFormSubmit, setIsFormSubmit]       = useState(false);
  const [chefRoleOption, setChefRoleOption]   = useState(null); // {value, label}
  const [rolesOptionList, setRolesOptionList] = useState([]);   // filtered: Chef only
  const [roleSelectInstanceId] = useState(
    () => `chef-role-${Math.random().toString(36).slice(2)}`
  );

  useEffect(() => {
    if (!activeDrawer) return;
    formReset();

    // Branch guard: let the user know early if no branch is selected
    const effBranch = getEffectiveBranchId();
    if (!effBranch) {
      ToastMe('Select a branch (top bar) before creating/updating a Chef.', 'warning');
    } else {
      // Optional: stick it into a hidden field for visibility/debug if you like
      // setValue('default_branch_id', effBranch);
      // console.log('🍃 Effective branch (Chef):', effBranch);
    }

    fetchChefRoleOnly().then(() => {
      if (editDataId) getDataById(editDataId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDrawer]);

  // If chefRoleOption arrives after data, force role_id again
  useEffect(() => {
    if (chefRoleOption?.value) {
      setValue('role_id', chefRoleOption.value, { shouldValidate: true });
    }
  }, [chefRoleOption, setValue]);

  const formReset = () => {
    reset();
    setValue('status', '1');
    setItemStatus('1');
    setRolesOptionList([]); // clear to avoid showing stale roles
    setChefRoleOption(null);
  };

  const handleDrawerClose = () => {
    formReset();
    setActiveDrawer(false);
    document.body.style.overflowY = 'auto';
    getTableData();
  };

  const handleChangeStatus = (e) => {
    setValue('status', e.target.value);
    setItemStatus(e.target.value);
  };

  // Fetch roles, then filter to Chef only
  const fetchChefRoleOnly = async () => {
    setIsLoading(true);
    try {
      const params = {
        search: '',
        page: 1,
        limit: 1000,
        extra_filter: {
          $or: [
            { name:  { $regex: 'chef', $options: 'i' } },
            { value: { $regex: 'chef', $options: 'i' } },
            { key:   { $regex: 'chef', $options: 'i' } },
          ],
        },
        _ts: Date.now(), // cache bust
      };
      const res = await dispatch(getRoleService(params));
      const allRoles = res?.data?.data?.data || [];

      const chefOnly = allRoles.filter(containsChef);

      if (!chefOnly.length) {
        setRolesOptionList([]);
        setChefRoleOption(null);
        setValue('role_id', '');
        ToastMe('Chef role not found. Please create a role named "Chef".', 'error');
        return;
      }

      const options = chefOnly.map((r) => ({ value: r._id, label: r.name || 'Chef' }));
      setRolesOptionList(options);
      setChefRoleOption(options[0]);
      setValue('role_id', options[0].value, { shouldValidate: true });
    } catch ({ message, errorData }) {
      validationErrors(errorData, message);
    } finally {
      setIsLoading(false);
    }
  };

  const getDataById = (editId) => {
    setIsLoading(true);
    dispatch(getByIdAdminService(editId))
      .then((res) => {
        const data = res?.data?.data;
        if (!data) return;

        setValue('name', data.name || '');
        setValue('email', data.email || '');
        setValue('phone_no', data.phone_no || '');
        setValue('status', String(data.status || 1));
        setItemStatus(String(data.status || 1));

        setValue('password', '');
        setValue('confirm_password', '');

        // If a role exists, still force to Chef when our option is known
        const existingRoleId = data?.role_id?._id || data?.role_id || '';
        if (chefRoleOption?.value) {
          setValue('role_id', chefRoleOption.value);
        } else if (existingRoleId) {
          setValue('role_id', existingRoleId);
        }

        setValue('salary', data.salary || '');
        setValue(
          'contractStartDate',
          data.contractStartDate
            ? new Date(data.contractStartDate).toISOString().split('T')[0]
            : ''
        );
        setValue(
          'contractEndDate',
          data.contractEndDate
            ? new Date(data.contractEndDate).toISOString().split('T')[0]
            : ''
        );
        setValue('holidayDays', Array.isArray(data.holidayDays) ? data.holidayDays : []);
        setValue('defaultInTime', data.defaultInTime || '');
        setValue('defaultOutTime', data.defaultOutTime || '');
        setValue('penaltyRate', data.penaltyRate || '');
        setValue('biometricId', data.biometricId || '');

        // Branch visibility check (optional)
        const effectiveBranch = getEffectiveBranchId();
        const hasBranch =
          Array.isArray(data.branch_ids) &&
          effectiveBranch &&
          data.branch_ids.some((b) => String(b) === String(effectiveBranch));

        if (effectiveBranch && !hasBranch) {
          console.warn(
            `⚠️ Editing Chef not assigned to current branch ${effectiveBranch}. Saving will attach this branch.`
          );
          ToastMe('Note: This user is not under the current branch; saving will attach it.', 'info');
        }
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  const onSubmit = async (formData) => {
    // Safety net: force Chef role on submit
    if (chefRoleOption?.value) formData.role_id = chefRoleOption.value;

    if (formData.confirm_password !== formData.password) {
      ToastMe('Password and confirm password do not match', 'error');
      return;
    }

    // ── BRANCH ENFORCEMENT ─────────────────────────────
    const effectiveBranch = getEffectiveBranchId();
    if (effectiveBranch) {
      formData.branch_ids = Array.isArray(formData.branch_ids) && formData.branch_ids.length
        ? Array.from(new Set([...formData.branch_ids.map(String), String(effectiveBranch)]))
        : [effectiveBranch];
      formData.default_branch_id = effectiveBranch;
      console.log('🍃 Using effective branch for Chef:', effectiveBranch);
    } else {
      ToastMe('Select a branch (top bar) before saving a Chef.', 'warning');
      // You can hard-stop if you want:
      // return;
    }
    // ──────────────────────────────────────────────────

    setIsFormSubmit(true);
    formData.isSuperAdmin = 0;
    formData.contractStartDate = formData.contractStartDate || null;
    formData.contractEndDate = formData.contractEndDate || null;
    formData.holidayDays = Array.isArray(formData.holidayDays) ? formData.holidayDays : [];

    const svc = editDataId ? updateAdminService(editDataId, formData) : addAdminService(formData);

    dispatch(svc)
      .then((res) => {
        if (!editDataId) {
          const branchInfo = effectiveBranch ? ` (branch ${effectiveBranch})` : '';
          ToastMe(`Chef created successfully${branchInfo}.`, 'success');
        } else {
          ToastMe('Chef updated successfully', 'success');
        }
        formReset();
        setIsFormSubmit(false);
        handleDrawerClose();
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsFormSubmit(false);
      });
  };

  const selectPortalProps = {
    menuPortalTarget: typeof window !== 'undefined' ? document.body : null,
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
  };

  return (
    <SidebarDrawer
      icon="lab lab-add-circle-line"
      titleText={titleText}
      activeDrawer={activeDrawer}
      handleDrawerClose={handleDrawerClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* NAME */}
          <Controller
            control={control}
            rules={{ required: true, minLength: 2, maxLength: 20 }}
            name="name"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title required"
                size="sm"
                labelText="Name"
                type="text"
                errorData={{ minLength: '2', maxLength: '20' }}
                errorType={errors?.name?.type}
              />
            )}
          />

          {/* EMAIL */}
          <Controller
            control={control}
            rules={{ required: true, pattern: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/ }}
            name="email"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title required"
                size="sm"
                labelText="Email"
                type="text"
                errorData={{ minLength: '6', maxLength: '50' }}
                errorType={errors?.email?.type}
              />
            )}
          />

          {/* PHONE */}
          <Controller
            control={control}
            rules={{ required: true, minLength: 7, maxLength: 20 }}
            name="phone_no"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title required"
                size="sm"
                labelText="Phone"
                type="text"
                errorData={{ minLength: '7', maxLength: '20' }}
                errorType={errors?.phone_no?.type}
              />
            )}
          />

          {/* STATUS RADIO */}
          <div className="form-input-item">
            <label className="db-field-title required" htmlFor="active">
              Status
            </label>
            <div className="db-field-radio-group">
              <input
                type="radio"
                id="active"
                name="status"
                value="1"
                checked={itemStatus === '1'}
                onChange={handleChangeStatus}
              />
              <label htmlFor="active">Active</label>
              <input
                type="radio"
                id="inactive"
                name="status"
                value="2"
                checked={itemStatus === '2'}
                onChange={handleChangeStatus}
              />
              <label htmlFor="inactive">Inactive</label>
            </div>
          </div>

          {/* PASSWORD */}
          <Controller
            control={control}
            rules={
              editDataId
                ? { minLength: 6, maxLength: 20 }
                : { required: true, minLength: 6, maxLength: 20 }
            }
            name="password"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName={editDataId ? '' : 'db-field-title required'}
                type="password"
                labelText="New Password"
                autoComplete="off"
                errorData={{ minLength: '6', maxLength: '20' }}
                errorType={errors?.password?.type}
              />
            )}
          />

          {/* CONFIRM PASSWORD */}
          <Controller
            control={control}
            rules={
              editDataId
                ? { minLength: 6, maxLength: 20 }
                : { required: true, minLength: 6, maxLength: 20 }
            }
            name="confirm_password"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName={editDataId ? '' : 'db-field-title required'}
                type="password"
                labelText="Confirm Password"
                autoComplete="off"
                errorData={{ minLength: '6', maxLength: '20' }}
                errorType={errors?.confirm_password?.type}
              />
            )}
          />

          {/* ROLE (Chef-only) */}
          <div className="form-input-item">
            <label className="db-field-title required">Role</label>

            {LOCK_ROLE_UI ? (
              <>
                <div className="inline-flex items-center px-3 py-2 rounded bg-emerald-50 text-emerald-800 text-sm border border-emerald-200">
                  {chefRoleOption?.label || 'Chef'}
                </div>
                <Controller
                  name="role_id"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <input type="hidden" {...field} value={chefRoleOption?.value || getValues('role_id') || ''} readOnly />
                  )}
                />
              </>
            ) : (
              <Controller
                control={control}
                name="role_id"
                rules={{ required: true }}
                render={({ field }) => (
                  <InputSelect
                    id="role_id"
                    labelText=""
                    instanceId={roleSelectInstanceId}
                    key={`chef-role-${rolesOptionList.length}-${field.value || 'none'}`}
                    options={rolesOptionList}
                    value={rolesOptionList.find((o) => o.value === field.value) || chefRoleOption || null}
                    onChange={(opt) => field.onChange(opt ? opt.value : '')}
                    errorType={errors?.role_id?.type}
                    onMenuOpen={() => {
                      console.log('[Chef Drawer] role options shown in dropdown:', rolesOptionList);
                    }}
                    {...selectPortalProps}
                  />
                )}
              />
            )}
          </div>

          {/* SALARY */}
          <Controller
            control={control}
            name="salary"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title required"
                size="sm"
                labelText="Salary"
                type="number"
                errorData={{ required: 'Salary is required' }}
                errorType={errors?.salary?.type}
              />
            )}
          />

          {/* BIOMETRIC ID */}
          <Controller
            control={control}
            name="biometricId"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title"
                size="sm"
                labelText="Biometric ID"
                type="text"
              />
            )}
          />

          {/* CONTRACT START DATE */}
          <Controller
            control={control}
            name="contractStartDate"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title required"
                size="sm"
                labelText="Contract Start Date"
                type="date"
                errorData={{ required: 'Contract start date is required' }}
                errorType={errors?.contractStartDate?.type}
              />
            )}
          />

          {/* CONTRACT END DATE */}
          <Controller
            control={control}
            name="contractEndDate"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title required"
                size="sm"
                labelText="Contract End Date"
                type="date"
                errorData={{ required: 'Contract end date is required' }}
                errorType={errors?.contractEndDate?.type}
              />
            )}
          />

          {/* HOLIDAY DAYS */}
          <Controller
            control={control}
            name="holidayDays"
            render={({ field }) => {
              const multiSelectVal = (field.value || []).map((num) =>
                dayOfWeekOptions.find((opt) => opt.value === num) || { value: num, label: `Day ${num}` }
              );
              return (
                <Select
                  isMulti
                  placeholder="Select Holiday Days"
                  value={multiSelectVal}
                  onChange={(selectedOptions) => {
                    const newNums = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
                    field.onChange(newNums);
                  }}
                  options={dayOfWeekOptions}
                  styles={selectPortalProps.styles}
                  menuPortalTarget={selectPortalProps.menuPortalTarget}
                  instanceId={`chef-holidays-${activeDrawer ? 'open' : 'closed'}`}
                />
              );
            }}
          />

          {/* DEFAULT IN/OUT */}
          <Controller
            control={control}
            name="defaultInTime"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title"
                size="sm"
                labelText="Default In Time (HH:mm)"
                type="time"
              />
            )}
          />

          <Controller
            control={control}
            name="defaultOutTime"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title"
                size="sm"
                labelText="Default Out Time (HH:mm)"
                type="time"
              />
            )}
          />

          {/* PENALTY RATE */}
          <Controller
            control={control}
            name="penaltyRate"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title"
                size="sm"
                labelText="Penalty Rate (%)"
                type="number"
              />
            )}
          />
        </div>

        <div className="form-col-12">
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="submit"
              className="db-btn py-2 text-white bg-primary"
              disabled={isFormSubmit || isLoading}
            >
              <i className="lab lab-save"></i>
              <span>Save</span>
              {isFormSubmit && (
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
            </button>
            <button
              type="button"
              className="modal-btn-outline modal-close"
              onClick={handleDrawerClose}
            >
              <i className="lab lab-close"></i>
              <span>Close</span>
            </button>
          </div>
        </div>
      </form>
    </SidebarDrawer>
  );
};

export default AddEditDrawer;
